import { firestore } from 'firebase/app'
import { FirestoreQuery } from '../Firestore/FirestoreQuery'
import { Reminder } from './Reminder'
import { ReminderMapper } from './ReminderMapper'

export class ReminderCollection {
  protected collection: string = 'reminder'

  constructor(protected db: firestore.Firestore, protected batch?: firestore.WriteBatch) {}

  watchRemindersByRestaurantCompanyId(restaurantCompanyId: string, cb?: (reminders: Reminder[]) => void): Reminder[] {
    const query = this.initQuery()
    query.setQuery(this.getRemindersByRestaurantCompanyIdQuery(query, restaurantCompanyId))
    return query.watchList(cb)
  }

  watchRemindersByPriceListEntryId(priceListEntryId: string, cb?: (reminders: Reminder[]) => void): Reminder[] {
    const query = this.initQuery()
    query.setQuery(this.getRemindersByPriceListEntryIdQuery(query, priceListEntryId))
    return query.watchList(cb)
  }

  watch(id: string): { data: Reminder | null } {
    const query = this.initQuery()
    return query.watch(id)
  }

  get(id: string): Promise<Reminder | null> {
    const query = this.initQuery()
    return query.find(id)
  }

  async listRemindersByRestaurantCompanyId(restaurantCompanyId: string): Promise<Reminder[]> {
    const query = this.initQuery()
    query.setQuery(this.getRemindersByRestaurantCompanyIdQuery(query, restaurantCompanyId))
    return query.list()
  }

  async listRemindersByRestaurantCompanyIdAndVendorCompanyId(restaurantCompanyId: string, vendorCompanyId: string): Promise<Reminder[]> {
    const query = this.initQuery()
    query.setQuery(this.getRemindersByRestaurantCompanyIdAndVendorCompanyIdQuery(query, restaurantCompanyId, vendorCompanyId))
    return query.list()
  }

  async listRemindersByPriceListEntryId(priceListEntryId: string): Promise<Reminder []> {
    const query = this.initQuery()
    query.setQuery(this.getRemindersByPriceListEntryIdQuery(query, priceListEntryId))
    return  query.list();
  }

  async listRemindersByRestaurantCompanyIdAndPriceListEntryIdAndVendorCompanyId(restaurantCompanyId: string, priceListEntryId: string, vendorCompanyId: string): Promise<Reminder | null> {
    const query = this.initQuery()
    query.setQuery(this.getRemindersByRestaurantCompanyIdAndPriceListEntryIdAndVendorCompanyId(query, restaurantCompanyId, priceListEntryId, vendorCompanyId).limit(1));
    const reminders = await query.list()
    return reminders.length ? reminders[0] : null
  }

  // this is meant to be used for vendor users
  async listRemindersByPriceListEntryIdAndVendorCompanyId(priceListEntryId: string, vendorCompanyId: string): Promise<Reminder | null> {
    const query = this.initQuery()
    query.setQuery(this.getRemindersByPriceListEntryIdAndVendorCompanyId(query, priceListEntryId, vendorCompanyId).limit(1));
    const reminders = await query.list()
    return reminders.length ? reminders[0] : null
  }

  save(reminder: Reminder): Promise<Reminder> {
    reminder.text = reminder.text.trim()

    const query = this.initQuery()
    return query.save(reminder)
  }

  async delete(reminder: Reminder) {
    const query = this.initQuery();
    return query.delete(reminder)
  }

  protected initQuery(): FirestoreQuery<Reminder> {
    const query = new FirestoreQuery<Reminder>(this.db, new ReminderMapper())
    query.collection(this.collection)

    return query
  }

  protected getRemindersByRestaurantCompanyIdQuery(query: FirestoreQuery<Reminder>, restaurantCompanyId: string) {
    return query.collection(this.collection).where('restaurantCompanyId', '==', restaurantCompanyId)
  }

  protected getRemindersByRestaurantCompanyIdAndVendorCompanyIdQuery(query: FirestoreQuery<Reminder>, restaurantCompanyId: string, vendorCompanyId: string) {
    return query.collection(this.collection)
      .where('restaurantCompanyId', '==', restaurantCompanyId)
      .where('vendorCompanyId', '==', vendorCompanyId)
  }

  protected getRemindersByPriceListEntryIdQuery(query: FirestoreQuery<Reminder>, priceListEntryId: string) {
    return query.collection(this.collection).where('priceListEntryId', '==', priceListEntryId)
  }

  protected getRemindersByRestaurantCompanyIdAndPriceListEntryIdAndVendorCompanyId(query: FirestoreQuery<Reminder>, restaurantCompanyId: string, priceListEntryId: string, vendorCompanyId: String) {
    return query.collection(this.collection)
      .where('restaurantCompanyId', '==', restaurantCompanyId)
      .where('priceListEntryId', '==', priceListEntryId)
      .where('vendorCompanyId', '==', vendorCompanyId)
  }

  protected getRemindersByPriceListEntryIdAndVendorCompanyId(query: FirestoreQuery<Reminder>, priceListEntryId: string, vendorCompanyId: String) {
    return query.collection(this.collection)
      .where('priceListEntryId', '==', priceListEntryId)
      .where('vendorCompanyId', '==', vendorCompanyId)
  }

  protected getRemindersByFieldValue(query: FirestoreQuery<Reminder>, field: string, value: any) {
    return query.collection(this.collection).where(field, '==', value)
  }
}
